import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { BRAND, MODAL_TITLE_TEMPLATE } from "./constants";
import { usePageTitleContext } from "./page-title-context";

type Props = {
	/** Si el modal está abierto: sólo entonces se pisa el título de pestaña. */
	open: boolean;
	/**
	 * Acción que describe el modal (p.ej. "Set Ladle State"). Se le añade el
	 * `entityName` del contexto → "Set Ladle State 207-A".
	 */
	helmetSuffix?: string;
	/**
	 * Nombre del modal tal cual, para modales que ya nombran su entidad en el
	 * título (no se le añade `entityName`). Se ignora si hay `helmetSuffix`.
	 */
	title?: string;
};

/**
 * Compone y aplica el título de pestaña de un modal: `<nombre del modal> -
 * <página donde se abrió> - NSB Equipment`. Extraído de `<Modal>` para que los
 * modales que NO usan el wrapper compartido (MesfModal, `<Dialog>` crudo)
 * también actualicen la pestaña. Captura la página padre desde `document.title`
 * al abrir, ANTES de que su propio Helmet lo pise; al cerrar, Helmet restaura
 * el título de la página subyacente.
 */
export const ModalTabTitle = ({ open, helmetSuffix, title }: Props) => {
	const { entityName } = usePageTitleContext();
	const [capturedParent, setCapturedParent] = useState<string | null>(null);
	useEffect(() => {
		if (!open) {
			setCapturedParent(null);
			return;
		}
		const withoutBrand = document.title
			.replace(new RegExp(`\\s*[·\\-]\\s*${BRAND}\\s*$`), "")
			.trim();
		setCapturedParent(withoutBrand === BRAND ? "" : withoutBrand);
	}, [open]);

	// Con `helmetSuffix` (acción) se le añade la entidad del contexto; si no, se
	// usa `title` tal cual (modales que ya nombran la entidad).
	const modalName = helmetSuffix
		? [helmetSuffix, entityName].filter(Boolean).join(" ")
		: title || undefined;
	const modalTabTitle = modalName
		? [modalName, capturedParent || undefined].filter(Boolean).join(" · ")
		: undefined;

	if (!open || capturedParent === null || !modalTabTitle) return null;
	return <Helmet titleTemplate={MODAL_TITLE_TEMPLATE} title={modalTabTitle} />;
};
